import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "../ui/button";
import { 
  Plus, Lock, Unlock, TriangleAlert, UserCog, 
  Users, ShieldCheck, Globe, X, Trash2, UsersRound, Search, Check 
} from "lucide-react";
import { cn } from "../ui/utils";
import { getRandomAvatarColor, getUserInitials } from "../../utils/avatar-utils";

interface UserInfo {
  id: string;
  name: string;
  email: string;
  initials: string;
  color: string;
  role?: string;
  archived?: boolean;
}

interface FormInfo {
  id: string;
  name: string;
  version: number;
  status: 'Published' | 'Draft' | 'Retired' | string;
  isRetired?: boolean;
}

export interface ServiceDeskConfig {
  id: string;
  name: string;
  description: string;
  themeColor?: string;
  submissionControl: 'open' | 'restricted';
  teamAdmin: UserInfo;
  members: UserInfo[];
  authorizedRequesters?: UserInfo[];
  isRestricted: boolean;
  isRetired: boolean;
  forms: FormInfo[];
}

interface ServiceDeskDetailsProps {
  config: ServiceDeskConfig;
  onUpdateAdmin: () => void;
  onAddMember: () => void;
  onRemoveMember: (email: string) => void;
  onToggleRestriction: (restricted: boolean) => void;
  onAddAuthorizedRequester: () => void;
  onRemoveAuthorizedRequester: (email: string) => void;
  onAddForm: () => void;
  onEditForm: (formId: string) => void;
  onRemoveForm: (formId: string) => void;
  onRetire: () => void;
}

export const ServiceDeskDetails: React.FC<ServiceDeskDetailsProps> = ({
  config,
  onUpdateAdmin,
  onAddMember,
  onRemoveMember,
  onToggleRestriction,
  onAddAuthorizedRequester,
  onRemoveAuthorizedRequester,
  onAddForm,
  onEditForm,
  onRemoveForm,
  onRetire
}) => {
  const navigate = useNavigate();
  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div>
        <h2 className="mb-4 text-2xl font-bold">{config.name}</h2>
        <p className="text-muted-foreground">{config.description}</p>
      </div>

      {/* Team Administrator */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <UserCog className="w-5 h-5" />
          <h3 className="text-lg font-bold">Team Administrator</h3>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div 
              className={cn(
                "w-10 h-10 text-base rounded-full flex items-center justify-center text-white font-medium shadow-sm", 
                getRandomAvatarColor(config.teamAdmin.name)
              )} 
              title={config.teamAdmin.name}
            >
              {getUserInitials(config.teamAdmin.name)}
            </div>
            <div>
              <div className="font-medium">{config.teamAdmin.name}</div>
              <div className="text-sm text-muted-foreground">{config.teamAdmin.email}</div>
            </div>
            {!config.isRetired && (
              <button 
                onClick={onUpdateAdmin}
                className="ml-auto text-sm text-primary hover:underline font-medium"
              >
                Change
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Team Members */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            <h3 className="text-lg font-bold">Team Members</h3>
          </div>
          {!config.isRetired && (
            <div className="flex items-center">
              <Button 
                onClick={onAddMember}
                className="hidden sm:flex items-center gap-2 h-9 font-bold"
              >
                <Plus className="w-4 h-4" />
                Add Member
              </Button>
              <Button 
                onClick={onAddMember}
                size="icon"
                className="sm:hidden h-10 w-10 shadow-md"
              >
                <Plus className="w-5 h-5" />
              </Button>
            </div>
          )}
        </div>
        <div className="space-y-2">
          {config.members.map((member) => (
            <div key={member.email} className="bg-card border border-border rounded-lg p-3 sm:p-4 flex items-center gap-2 sm:gap-3 shadow-sm hover:border-primary/20 transition-colors">
              <div 
                className={cn(
                  "flex shrink-0 w-7 h-7 sm:w-8 sm:h-8 text-[10px] sm:text-sm rounded-full items-center justify-center text-white font-medium shadow-sm", 
                  getRandomAvatarColor(member.name)
                )} 
                title={member.name}
              >
                {getUserInitials(member.name)}
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="font-bold text-sm sm:text-base text-gray-900 truncate">{member.name}</div>
                <div className="text-[11px] sm:text-sm text-muted-foreground truncate">{member.email}</div>
              </div>
              <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                <div className={cn(
                    "px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold uppercase tracking-wider shadow-sm border",
                    member.role === 'Editor' 
                        ? "bg-blue-50 text-blue-700 border-blue-100" 
                        : "bg-gray-100 text-gray-600 border-gray-200"
                )}>
                    {member.role}
                </div>
                {!config.isRetired && (
                  <button 
                    onClick={() => onRemoveMember?.(member.email)}
                    className="p-1.5 sm:p-2 hover:bg-red-50 text-muted-foreground hover:text-red-500 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Authorized Requesters */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-5 h-5" />
          <h3 className="text-lg font-bold">Authorized Requesters</h3>
        </div>
        <div className="bg-card border border-border rounded-lg p-4 space-y-4 shadow-sm">
          <div>
            <label className="block mb-3 text-sm font-medium">Access Level</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button 
                onClick={() => onToggleRestriction?.(false)}
                disabled={config.isRetired}
                className={cn(
                  "flex items-center gap-3 p-4 border rounded-lg transition-all text-left",
                  config.isRetired
                    ? !config.isRestricted
                      ? "border-gray-300 bg-gray-50 shadow-sm ring-1 ring-gray-300 opacity-90"
                      : "border-border opacity-50"
                    : !config.isRestricted 
                      ? "border-primary bg-primary/5 shadow-sm ring-1 ring-primary text-foreground" 
                      : "border-border hover:border-primary/50 text-foreground",
                  config.isRetired && "cursor-not-allowed"
                )}
              >
                <div className={cn(
                  "p-2 rounded-lg",
                  !config.isRestricted 
                    ? config.isRetired ? "bg-gray-200 text-gray-500" : "bg-primary/10 text-primary" 
                    : "bg-muted text-muted-foreground"
                )}>
                  <Globe className="w-5 h-5" />
                </div>
                <div className="flex-1 text-left">
                  <div className={cn("font-medium mb-1", config.isRetired && !config.isRestricted ? "text-gray-700" : "text-gray-900")}>Open to All</div>
                  <div className="text-xs text-muted-foreground">Anyone can submit requests</div>
                </div>
                {config.isRetired && !config.isRestricted && (
                  <Check className="w-5 h-5 text-gray-400" />
                )}
              </button>

              <button 
                onClick={() => onToggleRestriction?.(true)}
                disabled={config.isRetired}
                className={cn(
                  "flex items-center gap-3 p-4 border rounded-lg transition-all text-left",
                  config.isRetired
                    ? config.isRestricted
                      ? "border-gray-300 bg-gray-50 shadow-sm ring-1 ring-gray-300 opacity-90"
                      : "border-border opacity-50"
                    : config.isRestricted 
                      ? "border-primary bg-primary/5 shadow-sm ring-1 ring-primary text-foreground" 
                      : "border-border hover:border-primary/50 text-foreground",
                  config.isRetired && "cursor-not-allowed"
                )}
              >
                <div className={cn(
                  "p-2 rounded-lg",
                  config.isRestricted 
                    ? config.isRetired ? "bg-gray-200 text-gray-500" : "bg-primary/10 text-primary" 
                    : "bg-muted text-muted-foreground"
                )}>
                  <Lock className="w-5 h-5" />
                </div>
                <div className="flex-1 text-left">
                  <div className={cn("font-medium mb-1", config.isRetired && config.isRestricted ? "text-gray-700" : "text-gray-900")}>Restricted</div>
                  <div className="text-xs text-muted-foreground">Specific users only</div>
                </div>
                {config.isRetired && config.isRestricted && (
                  <Check className="w-5 h-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {!config.isRestricted && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 animate-in fade-in slide-in-from-top-1 duration-200">
                <Globe className="w-4 h-4" />
                <span>This service desk is open to all employees in the organization</span>
            </div>
          )}

          {config.isRestricted && (
            <div className="mt-4 pt-4 border-t border-border animate-in fade-in slide-in-from-top-2 duration-300">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium">Authorized Users ({config.authorizedRequesters?.length || 0})</label>
                {!config.isRetired && (
                  <div className="flex items-center">
                    <Button 
                      onClick={onAddAuthorizedRequester}
                      className="hidden sm:flex items-center gap-2 h-9 font-bold"
                    >
                      <Plus className="w-3 h-3" />
                      Add User
                    </Button>
                    <Button 
                      onClick={onAddAuthorizedRequester}
                      size="icon"
                      className="sm:hidden h-10 w-10 shadow-md"
                    >
                      <Plus className="w-5 h-5" />
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                {config.authorizedRequesters && config.authorizedRequesters.length > 0 ? (
                  config.authorizedRequesters.map((user) => (
                    <div key={user.email} className="bg-white border border-border rounded-lg p-2.5 sm:p-3 flex items-center gap-2 sm:gap-3 shadow-sm hover:border-primary/20 transition-colors">
                      <div 
                        className={cn(
                          "flex shrink-0 w-7 h-7 sm:w-8 sm:h-8 text-[10px] sm:text-xs rounded-full items-center justify-center text-white font-medium shadow-sm", 
                          getRandomAvatarColor(user.name)
                        )} 
                        title={user.name}
                      >
                        {getUserInitials(user.name)}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="font-bold text-sm text-gray-900 truncate">{user.name}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{user.email}</div>
                      </div>
                      {!config.isRetired && (
                        <button 
                          onClick={() => onRemoveAuthorizedRequester?.(user.email)}
                          className="p-1.5 hover:bg-red-50 text-muted-foreground hover:text-red-500 rounded-lg transition-colors shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground text-sm bg-muted rounded-lg border border-dashed border-border/50 shadow-inner">
                    <Lock className="w-8 h-8 mx-auto mb-2 opacity-50 text-muted-foreground" />
                    <p className="font-bold text-gray-900">No authorized requesters yet</p>
                    <p className="text-xs mt-1">Add users who can submit requests to this service desk</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Request Forms */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">Request Forms</h3>
          {!config.isRetired && (
            <div className="flex items-center">
              <Button 
                onClick={onAddForm}
                className="hidden sm:flex items-center gap-2 h-9 font-bold"
              >
                <Plus className="w-4 h-4" />
                Add Request Form
              </Button>
              <Button 
                onClick={onAddForm}
                size="icon"
                className="sm:hidden h-10 w-10 shadow-md"
              >
                <Plus className="w-5 h-5" />
              </Button>
            </div>
          )}
        </div>
        <div className="space-y-2">
          {config.forms.filter(f => !f.isRetired).map((form) => (
            <div 
                key={form.id} 
                onClick={() => !config.isRetired && navigate(`/service-desk/${config.id}/forms/${form.id}/edit`)}
                className={cn(
                  "bg-card border border-border rounded-lg p-4 flex items-center justify-between transition-all shadow-sm group",
                  !config.isRetired ? "hover:bg-accent/50 cursor-pointer" : "cursor-default opacity-80"
                )}
            >
              <div>
                <div className={cn("font-medium transition-colors", !config.isRetired && "group-hover:text-primary")}>{form.name}</div>
                <div className="text-sm text-muted-foreground">Version {form.version} • {form.status}</div>
              </div>
              <div className="flex items-center gap-2">
                {!config.isRetired && (
                  <>
                    <button 
                      onClick={(e) => {
                          e.stopPropagation();
                          onEditForm?.(form.id);
                      }}
                      className="text-sm text-primary hover:underline px-2 py-1 font-medium"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={(e) => {
                          e.stopPropagation();
                          onRemoveForm?.(form.id);
                      }}
                      className="p-2 hover:bg-red-50 text-muted-foreground hover:text-red-500 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Danger Zone */}
      {!config.isRetired && (
        <div>
          <h3 className="mb-4 text-red-600 font-bold">Danger Zone</h3>
          <div className="bg-card border border-red-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-red-50 rounded-lg">
                <TriangleAlert className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium mb-1">Retire Service Desk</h4>
                <p className="text-sm text-muted-foreground mb-4">Once retired, this service desk will no longer be accessible. This action cannot be undone.</p>
                <button 
                  onClick={onRetire}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-bold shadow-md active:scale-95"
                >
                  <Trash2 className="w-4 h-4" />
                  Retire Service Desk
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
